import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { getAdminToken } from '../components/AdminAuthGate';
import CMSRenderer, { DEFAULT_THEME, normalizeTheme, splitThemeAndBlocks } from '../components/cms/CMSRenderer';

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

function createBlock(type) {
  const id = uid();
  if (type === 'heading') return { id, type, level: 2, text: '新標題' };
  if (type === 'text') return { id, type, text: '請輸入內容...' };
  if (type === 'image') return { id, type, url: '', alt: '', radius: 10 };
  return { id, type: 'button', label: '按鈕文字', url: 'https://', variant: 'primary', newTab: true };
}

const THEME_PRESETS = {
  beige: {
    preset: 'beige',
    background: '#f8f3ea',
    surface: '#fffaf2',
    text: '#3b2c24',
    primary: '#7b4f2f',
    secondary: '#a67c5b',
    cardBorder: '#e8dccb',
    radius: 14,
  },
  coffee: {
    preset: 'coffee',
    background: '#f1e6d8',
    surface: '#fff8ef',
    text: '#2e221a',
    primary: '#6c4125',
    secondary: '#9d6c48',
    cardBorder: '#dfcfbd',
    radius: 14,
  },
  light: {
    preset: 'light',
    background: '#fcf8f1',
    surface: '#ffffff',
    text: '#46372d',
    primary: '#8a5b3d',
    secondary: '#b48a6b',
    cardBorder: '#ecdccc',
    radius: 14,
  },
};

function extractTheme(content) {
  return splitThemeAndBlocks(content).theme;
}

function upsertTheme(content, theme) {
  const blocks = Array.isArray(content) ? content.filter((b) => b?.type !== 'theme') : [];
  return [{ type: 'theme', ...normalizeTheme(theme) }, ...blocks];
}

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function CMSAdmin() {
  const [pages, setPages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({
    id: null,
    title: '',
    slug: '',
    seo_title: '',
    seo_desc: '',
    status: 'draft',
    content: upsertTheme([], DEFAULT_THEME),
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedId) || null,
    [pages, selectedId]
  );
  const publishUrl = useMemo(() => {
    const slug = String(form.slug || '').trim();
    if (!slug) return '';
    return `${window.location.origin}/cms/${slug}`;
  }, [form.slug]);
  const previewData = useMemo(() => splitThemeAndBlocks(form.content), [form.content]);
  const displayBlocks = previewData.blocks;
  const pageTheme = previewData.theme;

  const loadPages = useCallback(async () => {
    const { data } = await axios.get('/api/cms/pages', { headers: authHeaders() });
    setPages(Array.isArray(data?.list) ? data.list : []);
  }, []);

  const loadDetail = useCallback(async (id) => {
    const { data } = await axios.get(`/api/cms/pages/${id}`, { headers: authHeaders() });
    const p = data?.page;
    if (!p) return;
    setForm({
      id: p.id,
      title: p.title || '',
      slug: p.slug || '',
      seo_title: p.seo_title || '',
      seo_desc: p.seo_desc || '',
      status: p.status || 'draft',
      content: upsertTheme(Array.isArray(p.content) ? p.content : [], extractTheme(p.content)),
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadPages();
      } catch (e) {
        setMsg(e?.response?.data?.error || '載入頁面清單失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadPages]);

  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        setLoading(true);
        await loadDetail(selectedId);
      } catch (e) {
        setMsg(e?.response?.data?.error || '載入頁面失敗');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId, loadDetail]);

  const handleCreatePage = async () => {
    const title = window.prompt('新頁面名稱');
    if (!title || !title.trim()) return;
    const slugInput = window.prompt('網址代稱 slug（可留空自動產生）', '');
    try {
      setSaving(true);
      const { data } = await axios.post(
        '/api/cms/pages',
        { title: title.trim(), slug: String(slugInput || '').trim() },
        { headers: authHeaders() }
      );
      await loadPages();
      if (data?.id) setSelectedId(data.id);
      setMsg('已建立新頁面');
    } catch (e) {
      setMsg(e?.response?.data?.error || '建立失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!form.id) return;
    try {
      setSaving(true);
      await axios.put(
        `/api/cms/pages/${form.id}`,
        {
          title: form.title,
          slug: form.slug,
          seo_title: form.seo_title,
          seo_desc: form.seo_desc,
          content: form.content,
        },
        { headers: authHeaders() }
      );
      await loadPages();
      setMsg('草稿已儲存');
    } catch (e) {
      setMsg(e?.response?.data?.error || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!form.id) return;
    try {
      setSaving(true);
      await handleSave();
      await axios.post(`/api/cms/pages/${form.id}/publish`, {}, { headers: authHeaders() });
      await loadPages();
      await loadDetail(form.id);
      setMsg('已發佈');
    } catch (e) {
      setMsg(e?.response?.data?.error || '發佈失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateTheme = (patch) => {
    setForm((prev) => {
      const current = extractTheme(prev.content);
      const nextTheme = normalizeTheme({ ...current, ...patch });
      return { ...prev, content: upsertTheme(prev.content, nextTheme) };
    });
  };

  const applyThemePreset = (preset) => {
    const p = THEME_PRESETS[preset] || THEME_PRESETS.beige;
    updateTheme(p);
  };

  const setDisplayBlocks = (updater) => {
    setForm((prev) => {
      const currentTheme = extractTheme(prev.content);
      const currentBlocks = splitThemeAndBlocks(prev.content).blocks;
      const nextBlocks = typeof updater === 'function' ? updater(currentBlocks) : currentBlocks;
      return { ...prev, content: upsertTheme(nextBlocks, currentTheme) };
    });
  };

  const addBlock = (type) => {
    setDisplayBlocks((blocks) => [...blocks, createBlock(type)]);
  };

  const updateBlock = (idx, patch) => {
    setDisplayBlocks((blocks) => {
      const arr = [...blocks];
      arr[idx] = { ...arr[idx], ...patch };
      return arr;
    });
  };

  const moveBlock = (idx, dir) => {
    setDisplayBlocks((blocks) => {
      const to = idx + dir;
      if (to < 0 || to >= blocks.length) return blocks;
      const arr = [...blocks];
      const cur = arr[idx];
      arr[idx] = arr[to];
      arr[to] = cur;
      return arr;
    });
  };

  const removeBlock = (idx) => {
    setDisplayBlocks((blocks) => {
      const arr = [...blocks];
      arr.splice(idx, 1);
      return arr;
    });
  };

  const handleImageUpload = async (idx, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await axios.post(
          '/api/cms/upload-image',
          { file: reader.result },
          { headers: authHeaders() }
        );
        if (data?.imageUrl) {
          updateBlock(idx, { url: data.imageUrl });
          setMsg('圖片上傳成功');
        }
      } catch (e) {
        setMsg(e?.response?.data?.error || '圖片上傳失敗');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '280px 1fr', gap: 16, maxWidth: '100%', minWidth: 0 }}>
      <aside style={{ border: '1px solid #ddd', borderRadius: 10, padding: 12, height: 'fit-content', background: '#fcfcff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <strong>頁面清單</strong>
          <button onClick={handleCreatePage} disabled={saving}>+ 新增</button>
        </div>
        {loading && <div style={{ color: '#666' }}>載入中...</div>}
        {pages.map((p) => (
          <button
            key={p.id}
            onClick={() => setSelectedId(p.id)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginBottom: 8,
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: '8px 10px',
              background: p.id === selectedId ? '#f0f7ff' : '#fff',
            }}
          >
            <div style={{ fontWeight: 700 }}>{p.title}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{p.slug}</div>
            <div style={{ fontSize: 12, color: p.status === 'published' ? '#0a7d28' : '#ad6a00' }}>
              {p.status === 'published' ? '已發佈' : '草稿'}
            </div>
          </button>
        ))}
      </aside>

      <section style={{ border: '1px solid #ddd', borderRadius: 10, padding: 14, background: '#fff' }}>
        {!form.id ? (
          <div style={{ color: '#666' }}>請先在左側選擇頁面，或新增一個頁面。</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr minmax(340px, 46%)', gap: 14, minWidth: 0 }}>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 10 }}>
                  <label>
                    標題
                    <input
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </label>
                  <label>
                    Slug（網址）
                    <input
                      value={form.slug}
                      onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </label>
                  <label>
                    SEO 標題
                    <input
                      value={form.seo_title}
                      onChange={(e) => setForm((p) => ({ ...p, seo_title: e.target.value }))}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </label>
                  <label>
                    SEO 描述
                    <textarea
                      value={form.seo_desc}
                      onChange={(e) => setForm((p) => ({ ...p, seo_desc: e.target.value }))}
                      rows={3}
                      style={{ width: '100%', marginTop: 4 }}
                    />
                  </label>
                </div>

                <div style={{ border: '1px solid #eadfce', borderRadius: 10, padding: 10, background: '#fffaf3' }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>網站視覺主題（米色 / 明亮 / 咖啡）</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                    <button onClick={() => applyThemePreset('beige')}>米色主題</button>
                    <button onClick={() => applyThemePreset('coffee')}>咖啡主題</button>
                    <button onClick={() => applyThemePreset('light')}>明亮主題</button>
                  </div>
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
                    <label>背景色 <input type="color" value={pageTheme.background} onChange={(e) => updateTheme({ background: e.target.value })} /></label>
                    <label>區塊底色 <input type="color" value={pageTheme.surface} onChange={(e) => updateTheme({ surface: e.target.value })} /></label>
                    <label>文字色 <input type="color" value={pageTheme.text} onChange={(e) => updateTheme({ text: e.target.value })} /></label>
                    <label>主按鈕色 <input type="color" value={pageTheme.primary} onChange={(e) => updateTheme({ primary: e.target.value })} /></label>
                    <label>次按鈕色 <input type="color" value={pageTheme.secondary} onChange={(e) => updateTheme({ secondary: e.target.value })} /></label>
                    <label>邊框色 <input type="color" value={pageTheme.cardBorder} onChange={(e) => updateTheme({ cardBorder: e.target.value })} /></label>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => addBlock('heading')}>+ 標題區塊</button>
                  <button onClick={() => addBlock('text')}>+ 文字區塊</button>
                  <button onClick={() => addBlock('image')}>+ 圖片區塊</button>
                  <button onClick={() => addBlock('button')}>+ 按鈕區塊</button>
                </div>

                <div style={{ display: 'grid', gap: 12 }}>
                  {displayBlocks.map((b, idx) => (
                    <div key={b.id || idx} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>{b.type}</strong>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => moveBlock(idx, -1)}>上移</button>
                          <button onClick={() => moveBlock(idx, 1)}>下移</button>
                          <button onClick={() => removeBlock(idx)}>刪除</button>
                        </div>
                      </div>

                      {b.type === 'heading' && (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <label>
                            標題層級
                            <select
                              value={b.level || 2}
                              onChange={(e) => updateBlock(idx, { level: Number(e.target.value) || 2 })}
                              style={{ marginLeft: 8 }}
                            >
                              <option value={1}>H1</option>
                              <option value={2}>H2</option>
                              <option value={3}>H3</option>
                            </select>
                          </label>
                          <textarea
                            rows={2}
                            value={b.text || ''}
                            onChange={(e) => updateBlock(idx, { text: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </div>
                      )}

                      {b.type === 'text' && (
                        <textarea
                          rows={5}
                          value={b.text || ''}
                          onChange={(e) => updateBlock(idx, { text: e.target.value })}
                          style={{ width: '100%' }}
                        />
                      )}

                      {b.type === 'image' && (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <input
                            placeholder="圖片網址"
                            value={b.url || ''}
                            onChange={(e) => updateBlock(idx, { url: e.target.value })}
                          />
                          <input
                            placeholder="替代文字 alt"
                            value={b.alt || ''}
                            onChange={(e) => updateBlock(idx, { alt: e.target.value })}
                          />
                          <label>
                            圖片圓角（0-32）
                            <input
                              type="number"
                              min={0}
                              max={32}
                              value={Number.isFinite(Number(b.radius)) ? Number(b.radius) : 10}
                              onChange={(e) => updateBlock(idx, { radius: Number(e.target.value) || 0 })}
                              style={{ marginLeft: 8 }}
                            />
                          </label>
                          <div
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const f = e.dataTransfer?.files?.[0];
                              if (f) handleImageUpload(idx, f);
                            }}
                            style={{ border: '1px dashed #b89c7a', borderRadius: 8, padding: 10, background: '#fff8ef', color: '#6f5036' }}
                          >
                            拖拉圖片到這裡上傳，或用下方選檔
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(idx, e.target.files?.[0])}
                          />
                        </div>
                      )}

                      {b.type === 'button' && (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <input
                            placeholder="按鈕文字"
                            value={b.label || ''}
                            onChange={(e) => updateBlock(idx, { label: e.target.value })}
                          />
                          <input
                            placeholder="連結網址"
                            value={b.url || ''}
                            onChange={(e) => updateBlock(idx, { url: e.target.value })}
                          />
                          <label>
                            按鈕風格
                            <select
                              value={b.variant || 'primary'}
                              onChange={(e) => updateBlock(idx, { variant: e.target.value })}
                              style={{ marginLeft: 8 }}
                            >
                              <option value="primary">主色按鈕</option>
                              <option value="secondary">次色按鈕</option>
                              <option value="outline">外框按鈕</option>
                            </select>
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={b.newTab !== false}
                              onChange={(e) => updateBlock(idx, { newTab: e.target.checked })}
                            />
                            新分頁開啟
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ position: 'sticky', top: 12, alignSelf: 'start', border: '1px solid #eadfce', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ padding: '8px 10px', background: '#fff8ef', color: '#6a4a31', fontWeight: 700 }}>
                  所見即所得預覽（與前台同版型）
                </div>
                <div style={{ maxHeight: '72vh', overflow: 'auto' }}>
                  <CMSRenderer blocks={form.content} pageTitle={form.title} pageDesc={form.seo_desc} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={handleSave} disabled={saving}>{saving ? '處理中...' : '儲存草稿'}</button>
              <button onClick={handlePublish} disabled={saving}>{saving ? '處理中...' : '發佈'}</button>
              <button
                onClick={() => publishUrl && window.open(publishUrl, '_blank')}
                disabled={!publishUrl}
              >
                已發佈頁預覽
              </button>
              <button
                onClick={async () => {
                  if (!publishUrl) return;
                  try {
                    await navigator.clipboard.writeText(publishUrl);
                    setMsg('公開網址已複製');
                  } catch {
                    setMsg('複製失敗，請手動複製網址');
                  }
                }}
                disabled={!publishUrl}
              >
                複製公開網址
              </button>
              {selectedPage && (
                <span style={{ color: '#666', alignSelf: 'center' }}>
                  狀態：{selectedPage.status}
                </span>
              )}
            </div>

            <div style={{ marginTop: 10, padding: '8px 10px', background: '#f7f7f7', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>公開網址</div>
              <input value={publishUrl} readOnly style={{ width: '100%' }} />
            </div>
          </>
        )}

        {msg && <div style={{ marginTop: 12, color: '#0f3460', fontWeight: 600 }}>{msg}</div>}
      </section>
    </div>
  );
}
